import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Bell, BellOff, Loader2 } from "lucide-react";
import api, { fmtError } from "@/lib/api";

/**
 * Push-notification preferences. Reads + writes the current user's
 * `notification_prefs` map (key → bool). Falls back to an empty UI if the
 * backend doesn't expose the list yet.
 */
const CHANNELS = [
  { key: "friend_request",     i18n: "settings.notifFriendRequest" },
  { key: "friend_accepted",    i18n: "settings.notifFriendAccepted" },
  { key: "message",            i18n: "settings.notifMessage" },
  { key: "gift_received",      i18n: "settings.notifGift" },
  { key: "activity_invite",    i18n: "settings.notifActivityInvite" },
  { key: "activity_reminder",  i18n: "settings.notifActivityReminder" },
  { key: "id_review_status",   i18n: "settings.notifIdReview" },
];

export default function SettingsNotifications() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setPrefs(data.notification_prefs || {});
      } catch (e) {
        toast.error(fmtError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  // Optimistic toggle — flip locally, then PATCH; revert + toast on error.
  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await api.patch("/users/me", { notification_prefs: next });
    } catch (e) {
      setPrefs(prefs);  // revert
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-notifications-page">
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="notif-back" aria-label="Back">
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[28px] tracking-tight">{t("settings.notifications")}</h1>
          {saving && <Loader2 className="w-4 h-4 animate-spin text-[#7C828D] ml-2" />}
        </div>
      </div>

      <section className="px-5 pt-6">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-[#7C828D]" /></div>
        ) : (
          <ul className="divide-y divide-[#F0EDE3]">
            {CHANNELS.map((ch) => {
              const on = prefs[ch.key] !== false; // default ON unless explicitly disabled
              return (
                <li key={ch.key} className="flex items-center gap-4 py-4">
                  {on ? <Bell className="w-5 h-5 text-[#0A0B0D]" strokeWidth={2.5} /> : <BellOff className="w-5 h-5 text-[#7C828D]" strokeWidth={2.5} />}
                  <span className="flex-1 font-semibold">{t(ch.i18n)}</span>
                  <button
                    onClick={() => toggle(ch.key)}
                    role="switch"
                    aria-checked={on}
                    data-testid={`toggle-${ch.key}`}
                    className={`relative w-12 h-7 rounded-full transition-colors ${on ? "bg-[#00CED1]" : "bg-[#D8D3C2]"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
